import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Gateway, GatewayService } from '@wheere/api-site';
import {
  DemoTrajectory,
  DemoTrajectoryAssignmentResponse,
  DemoTrajectoryRequest,
  DemoTrajectoryService,
  Tracker,
  TrackerService,
} from '@wheere/api-tracker';
import { errorMessage } from '@wheere-front/shared-util';
import { firstValueFrom } from 'rxjs';
import { fallsOnSite, LatLon, siteAnchor, sitesUnder } from '../util/demo-trajectory';

/** Un tracker factice (env `fake`) et la trajectoire qu'il joue (`null` = boucle par défaut). */
export interface DemoFakeTracker {
  id: string;
  name: string;
  deviceId: string | null;
  siteId: string | null;
  siteName: string | null;
  trajectoryId: string | null;
}

/** Un site de démo : un site qui porte des trackers factices, avec son antenne 0. */
export interface DemoSite {
  id: string;
  name: string;
  /** Ville de l'adresse, pour distinguer deux sites au même nom. */
  place: string | null;
  anchor: LatLon | null;
  /** Gateways du site (fond de carte : gateways et antennes). */
  gateways: Gateway[];
  /** Lecture des gateways en échec : l'antenne 0 est inconnue, pas absente. */
  anchorFailed: boolean;
}

interface DemoTrajectoriesState {
  trajectories: DemoTrajectory[];
  fakeTrackers: DemoFakeTracker[];
  sites: DemoSite[];
  /** Ce qui est chargé : la bibliothèque entière ou un site. */
  scope: 'library' | `site:${string}` | null;
  loading: boolean;
  error: string | null;
}

const PAGE = 500;

const toFake = (t: Tracker): DemoFakeTracker => ({
  id: t.id!,
  name: t.name || t.deviceId || t.id!,
  deviceId: t.deviceId ?? null,
  siteId: t.site?.id ?? null,
  siteName: t.site?.name ?? null,
  trajectoryId: t.demoTrajectoryId ?? null,
});

/**
 * ADR-045 — trajectoires de démo : la bibliothèque (Outils › Trajectoires de démo) et le choix par
 * tracker factice (Site › Équipements › Trackers). Écritures réservées à PLATFORM_ADMIN (serveur).
 * Une trajectoire est « sur » un site quand tous ses points sont à moins de 300 m de son antenne 0.
 */
export const DemoTrajectoriesStore = signalStore(
  { providedIn: 'root' },
  withState<DemoTrajectoriesState>({
    trajectories: [],
    fakeTrackers: [],
    sites: [],
    scope: null,
    loading: false,
    error: null,
  }),
  withComputed(store => ({
    /** Sites sur lesquels tombe chaque trajectoire, par id de trajectoire. */
    sitesOf: computed<Record<string, DemoSite[]>>(() => {
      const out: Record<string, DemoSite[]> = {};
      for (const t of store.trajectories()) out[t.id] = sitesUnder(t.points, store.sites());
      return out;
    }),
    /** Trackers factices qui jouent chaque trajectoire, par id de trajectoire. */
    usedBy: computed<Record<string, DemoFakeTracker[]>>(() => {
      const out: Record<string, DemoFakeTracker[]> = {};
      for (const f of store.fakeTrackers()) {
        if (f.trajectoryId) (out[f.trajectoryId] ??= []).push(f);
      }
      return out;
    }),
  })),
  withMethods(store => {
    const demo = inject(DemoTrajectoryService);
    const trackers = inject(TrackerService);
    const gateways = inject(GatewayService);
    let inflight: { scope: string; promise: Promise<void> } | null = null;

    /** Factices (tous, ou d'un site) et, au passage, le nom et la ville de leurs sites. */
    const fakeTrackers = async (
      siteId?: string
    ): Promise<{ fakes: DemoFakeTracker[]; siteInfo: Map<string, { name: string; place: string | null }> }> => {
      const out: DemoFakeTracker[] = [];
      const siteInfo = new Map<string, { name: string; place: string | null }>();
      for (let page = 0; ; page++) {
        const res = await firstValueFrom(
          trackers.getTrackers(
            undefined,
            undefined,
            undefined,
            undefined,
            siteId ? [siteId] : undefined,
            undefined,
            undefined,
            page,
            PAGE,
            undefined,
            ['fake']
          )
        );
        for (const t of res.content ?? []) {
          if (!t.id) continue;
          out.push(toFake(t));
          if (t.site?.id && !siteInfo.has(t.site.id)) {
            siteInfo.set(t.site.id, { name: t.site.name ?? t.site.id, place: t.site.address?.city ?? null });
          }
        }
        if (res.last !== false || (res.content ?? []).length < PAGE) return { fakes: out, siteInfo };
      }
    };

    const siteOf = async (id: string, name: string, place: string | null): Promise<DemoSite> => {
      try {
        const gws = (await firstValueFrom(gateways.getGatewaysBySite(id))) ?? [];
        return { id, name, place, anchor: siteAnchor(gws), gateways: gws, anchorFailed: false };
      } catch {
        // Visible : l'option du site le dit, et la trajectoire n'y est pas proposée.
        return { id, name, place, anchor: null, gateways: [], anchorFailed: true };
      }
    };

    const load = (scope: DemoTrajectoriesState['scope'] & string, run: () => Promise<void>): Promise<void> => {
      // Appels simultanés (plusieurs cellules, la bulle d'aide) : un seul chargement.
      if (inflight?.scope === scope) return inflight.promise;
      patchState(store, { loading: true, error: null });
      const promise = run()
        .then(() => patchState(store, { scope }))
        .catch(e => patchState(store, { error: errorMessage(e, 'Trajectoires de démo illisibles'), scope: null }))
        .finally(() => {
          patchState(store, { loading: false });
          inflight = null;
        });
      inflight = { scope, promise };
      return promise;
    };

    return {
      /** Outils : toutes les trajectoires, tous les trackers factices et leurs sites (antenne 0). */
      loadLibrary(): Promise<void> {
        return load('library', async () => {
          const [list, { fakes, siteInfo }] = await Promise.all([
            firstValueFrom(demo.listDemoTrajectories()),
            fakeTrackers(),
          ]);
          // Sites de démo = sites qui portent des factices ; le Tracker liste porte nom et adresse du site.
          const sites = await Promise.all([...siteInfo.entries()].map(([id, s]) => siteOf(id, s.name, s.place)));
          sites.sort((a, b) => a.name.localeCompare(b.name));
          patchState(store, { trajectories: list ?? [], fakeTrackers: fakes, sites });
        });
      },

      /** Page du site : toutes les trajectoires, les factices du site, son antenne 0. */
      loadSite(siteId: string): Promise<void> {
        return load(`site:${siteId}`, async () => {
          const [list, { fakes, siteInfo }] = await Promise.all([
            firstValueFrom(demo.listDemoTrajectories()),
            fakeTrackers(siteId),
          ]);
          const info = siteInfo.get(siteId);
          const site = await siteOf(siteId, info?.name ?? siteId, info?.place ?? null);
          patchState(store, { trajectories: list ?? [], fakeTrackers: fakes, sites: [site] });
        });
      },

      /** Trajectoires proposées sur un site chargé (tous leurs points à moins de 300 m de l'antenne 0). */
      offeredOn(siteId: string): DemoTrajectory[] {
        const anchor = store.sites().find(s => s.id === siteId)?.anchor ?? null;
        return store.trajectories().filter(t => fallsOnSite(t.points, anchor));
      },

      /** Trajectoire jouée par un tracker factice chargé, `null` = boucle par défaut. */
      trajectoryOf(trackerId: string): string | null {
        return store.fakeTrackers().find(f => f.id === trackerId)?.trajectoryId ?? null;
      },

      async create(req: DemoTrajectoryRequest): Promise<DemoTrajectory> {
        const created = await firstValueFrom(demo.createDemoTrajectory(req));
        patchState(store, s => ({ trajectories: [...s.trajectories, created] }));
        return created;
      },

      async update(id: string, req: DemoTrajectoryRequest): Promise<DemoTrajectory> {
        const updated = await firstValueFrom(demo.updateDemoTrajectory(id, req));
        patchState(store, s => ({ trajectories: s.trajectories.map(t => (t.id === id ? updated : t)) }));
        return updated;
      },

      /** Supprime ; ses trackers repassent sur la boucle par défaut (le serveur le fait dans la même transaction). */
      async remove(id: string): Promise<void> {
        await firstValueFrom(demo.deleteDemoTrajectory(id));
        patchState(store, s => ({
          trajectories: s.trajectories.filter(t => t.id !== id),
          fakeTrackers: s.fakeTrackers.map(f => (f.trajectoryId === id ? { ...f, trajectoryId: null } : f)),
        }));
      },

      /** `PUT /trackers/demo-trajectory` : les trackers réels ou inconnus reviennent dans `skipped`. */
      async assign(trackerIds: string[], trajectoryId: string | null): Promise<DemoTrajectoryAssignmentResponse> {
        const res = await firstValueFrom(demo.assignDemoTrajectory({ trackerIds, trajectoryId }));
        const updated = new Set(res.updated ?? []);
        patchState(store, s => ({
          fakeTrackers: s.fakeTrackers.map(f => (updated.has(f.id) ? { ...f, trajectoryId } : f)),
        }));
        return res;
      },
    };
  })
);
